/**
 * "Possible duplicate" warning shown while a work item is being proposed — on
 * the New-work-item form and the AI-analysis review card. Both ask the same
 * question ("does an open item like this already exist in this project?") the
 * moment there's enough title to go on, so a duplicate can be caught before
 * it's created rather than merged away afterwards.
 */

import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

export type SimilarMatch = {
  id: string;
  title: string;
  gitlabIssueId: string | null;
  globalId: number;
  score: number;
};

/**
 * Debounced lookup of likely-duplicate open work items for a proposed
 * title/description in a project. Returns [] until there's a project and enough
 * title; re-queries as the inputs settle.
 */
export function useSimilarWorkItems(
  projectId: string,
  title: string,
  description?: string,
): SimilarMatch[] {
  const [matches, setMatches] = useState<SimilarMatch[]>([]);

  useEffect(() => {
    if (!projectId || title.trim().length < 3) {
      setMatches([]);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      const qs = new URLSearchParams({ projectId, title: title.trim() });
      if (description?.trim()) qs.set("description", description.slice(0, 2000));
      api
        .get<{ matches: SimilarMatch[] }>(`/api/work-items/similar?${qs.toString()}`)
        .then((r) => {
          if (alive) setMatches(r.matches ?? []);
        })
        .catch(() => {
          if (alive) setMatches([]);
        });
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [projectId, title, description]);

  return matches;
}

/**
 * The warning box. Renders nothing when there are no matches. `action` lets a
 * consumer add a per-item control (e.g. "Comment on this instead" on the
 * analysis card); omitted, each match is just a link to the existing item.
 */
export function SimilarWorkItemsWarning({
  matches,
  action,
}: {
  matches: SimilarMatch[];
  action?: (match: SimilarMatch) => ReactNode;
}) {
  if (matches.length === 0) return null;
  return (
    <div
      data-testid="similar-warning"
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/60 dark:bg-amber-950/30"
    >
      <div className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-300">
        <AlertTriangle className="h-4 w-4" />
        {matches.length === 1
          ? "A similar work item already exists"
          : `${matches.length} similar work items already exist`}
      </div>
      <ul className="mt-2 space-y-1.5">
        {matches.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-3">
            <Link
              to="/work-item/$id"
              params={{ id: String(m.globalId) }}
              target="_blank"
              rel="noreferrer"
              className="truncate underline decoration-dotted"
              data-testid="similar-match"
            >
              {`#${m.globalId} `}
              {m.title}
            </Link>
            {action?.(m)}
          </li>
        ))}
      </ul>
    </div>
  );
}
